import React, { useState, useEffect } from 'react';
import { TrendingUp, Target, Users, Award, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import MetricCard from '../../components/SalesTargets/MetricCard';
import ProgressChart from '../../components/SalesTargets/ProgressChart';

const SalesTargetDashboard = () => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState({
    summary: {},
    activeTargets: [],
    topPerformers: [],
    atRiskTargets: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const currentYear = new Date().getFullYear();

      // Fetch summary
      const summaryResponse = await axios.get(`/api/salestargets/summary?periodYear=${currentYear}`);
      
      // Fetch active targets with progress
      const targetsResponse = await axios.get(`/api/salestargets?status=Active&periodYear=${currentYear}`);
      const targets = Array.isArray(targetsResponse.data.targets) ? targetsResponse.data.targets : [];

      // Calculate progress for each target
      const targetsWithProgress = await Promise.all(
        targets.map(async (target) => {
          try {
            const progressResponse = await axios.get(`/api/salestargets/${target.TargetID}/progress`);
            const progress = progressResponse.data.progress || [];
            
            let totalWeight = 0;
            let weightedProgress = 0;
            
            progress.forEach(metric => {
              const weight = metric.Weight || 1;
              totalWeight += weight;
              weightedProgress += (parseFloat(metric.PercentComplete || 0) * weight);
            });
            
            const overallProgress = totalWeight > 0 ? (weightedProgress / totalWeight) : 0;
            
            return {
              ...target,
              progress,
              overallProgress: overallProgress.toFixed(2)
            };
          } catch (error) {
            return {
              ...target,
              progress: [],
              overallProgress: 0
            };
          }
        })
      );

      // Sort by progress
      const sortedTargets = targetsWithProgress.sort((a, b) => b.overallProgress - a.overallProgress);
      const topPerformers = sortedTargets.filter(t => t.overallProgress >= 80).slice(0, 5);
      const atRiskTargets = sortedTargets.filter(t => t.overallProgress < 50).slice(0, 5);

      setDashboardData({
        summary: summaryResponse.data.summary || {},
        activeTargets: targetsWithProgress,
        topPerformers,
        atRiskTargets
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const prepareProgressDistribution = () => {
    const ranges = [
      { name: 'Achieved (100%+)', count: 0 },
      { name: 'On Track (80-99%)', count: 0 },
      { name: 'At Risk (50-79%)', count: 0 },
      { name: 'Behind (<50%)', count: 0 }
    ];

    dashboardData.activeTargets.forEach(target => {
      const progress = parseFloat(target.overallProgress);
      if (progress >= 100) ranges[0].count++;
      else if (progress >= 80) ranges[1].count++;
      else if (progress >= 50) ranges[2].count++;
      else ranges[3].count++;
    });

    return ranges.map(r => ({ name: r.name, value: r.count }));
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
            <p className="text-sm text-gray-600 mt-4">Loading dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <Target className="w-6 h-6 mr-2" />
          Sales Target Dashboard
        </h1>
        <p className="text-sm text-gray-600 mt-1">
          Real-time overview of sales target performance for {new Date().getFullYear()}
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide">Active Targets</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">
                {dashboardData.summary.ActiveTargets || 0}
              </p>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg">
              <Target className="w-8 h-8 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide">On Track</p>
              <p className="text-3xl font-bold text-green-600 mt-2">
                {dashboardData.activeTargets.filter(t => t.overallProgress >= 80).length}
              </p>
            </div>
            <div className="p-3 bg-green-50 rounded-lg">
              <TrendingUp className="w-8 h-8 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide">At Risk</p>
              <p className="text-3xl font-bold text-yellow-600 mt-2">
                {dashboardData.activeTargets.filter(t => t.overallProgress >= 50 && t.overallProgress < 80).length}
              </p>
            </div>
            <div className="p-3 bg-yellow-50 rounded-lg">
              <AlertTriangle className="w-8 h-8 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide">Behind</p>
              <p className="text-3xl font-bold text-red-600 mt-2">
                {dashboardData.activeTargets.filter(t => t.overallProgress < 50).length}
              </p>
            </div>
            <div className="p-3 bg-red-50 rounded-lg">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <ProgressChart
          data={prepareProgressDistribution()}
          type="pie"
          title="Target Status Distribution"
          height={300}
        />
        <ProgressChart
          data={dashboardData.activeTargets.map(t => ({
            name: t.TargetName.length > 15 ? t.TargetName.substring(0, 15) + '...' : t.TargetName,
            actual: parseFloat(t.overallProgress),
            target: 100
          }))}
          type="progress"
          title="All Active Targets Progress"
          height={300}
        />
      </div>

      {/* Top Performers */}
      {dashboardData.topPerformers.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <Award className="w-5 h-5 mr-2 text-yellow-500" />
              Top Performers
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {dashboardData.topPerformers.map((target) => (
              <div
                key={target.TargetID}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/salestargets/${target.TargetID}`)}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-gray-900 mb-1">
                      {target.TargetName}
                    </h3>
                    <p className="text-xs text-gray-500">
                      {target.TargetLevel} • {target.PeriodType}
                    </p>
                  </div>
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {target.overallProgress}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="h-2 rounded-full bg-green-500"
                    style={{ width: `${Math.min(target.overallProgress, 100)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* At Risk Targets */}
      {dashboardData.atRiskTargets.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <AlertTriangle className="w-5 h-5 mr-2 text-red-500" />
              Targets Needing Attention
            </h2>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-red-200">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-red-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                      Target
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-700 uppercase">
                      Level
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-700 uppercase">
                      Progress
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {dashboardData.atRiskTargets.map((target) => (
                    <tr key={target.TargetID} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {target.TargetName}
                          </div>
                          <div className="text-xs text-gray-500">
                            {target.PeriodType} {target.PeriodYear}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center text-sm text-gray-900">
                        {target.TargetLevel}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center">
                          <div className="w-32">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs font-medium text-red-700">
                                {target.overallProgress}%
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className="h-2 rounded-full bg-red-500"
                                style={{ width: `${Math.min(target.overallProgress, 100)}%` }}
                              />
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => navigate(`/salestargets/${target.TargetID}`)}
                          className="text-sm text-blue-600 hover:text-blue-900 font-medium"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {dashboardData.activeTargets.length === 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <Target className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Active Targets</h3>
          <p className="text-sm text-gray-500 mb-6">
            Create your first sales target to start tracking performance
          </p>
          <button
            onClick={() => navigate('/salestargets/new')}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            Create Target
          </button>
        </div>
      )}
    </div>
  );
};

export default SalesTargetDashboard;


